import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { COLLECTION_LISTING_TYPE, visibleCollectionPieces } from '../../util/collections';
import { getProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, setCurrentUserHasOrders } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

/**
 * Billed-varianter til en samling: kort-størrelse til griddet og en større
 * variant til detalje-visningen, hvor stykket vises stort.
 */
export const collectionImageFields = listingImageConfig => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = listingImageConfig || {};
  const aspectRatio = aspectHeight / aspectWidth;

  return {
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

/**
 * Parametre til at HENTE stykker. Bemærk at `include` kun hører til her:
 * sendes den til images.upload, svarer API'et 500 (et billede har ingen
 * images-relation) - hold derfor felt-parametrene og query-parametrene adskilt.
 */
export const collectionImageParams = (listingImageConfig, options = {}) => {
  const { includeAuthor = true } = options;
  return {
    include: includeAuthor ? ['author', 'author.profileImage', 'images'] : ['images'],
    ...collectionImageFields(listingImageConfig),
  };
};

// ================ Async Thunks ================ //

const showCollectorPayloadCreator = ({ userId }, { dispatch, rejectWithValue, extra: sdk }) => {
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const showCollectorThunk = createAsyncThunk(
  'CollectionPage/showCollector',
  showCollectorPayloadCreator
);

const queryCollectionPayloadCreator = (
  { userId, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  return sdk.listings
    .query({
      author_id: userId,
      pub_listingType: COLLECTION_LISTING_TYPE,
      perPage: 100,
      ...collectionImageParams(config?.layout?.listingImage),
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      // Stykker samleren selv har fjernet, filtreres fra her - de er kun lukkede
      // i API'et og kan stadig nå at komme med i et cachet svar.
      const pieceRefs = visibleCollectionPieces(response.data.data).map(({ id, type }) => ({
        id,
        type,
      }));
      return { pieceRefs };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const queryCollectionThunk = createAsyncThunk(
  'CollectionPage/queryCollection',
  queryCollectionPayloadCreator
);

/**
 * "Spørg om det er til salg": starter en inquiry-transaktion på stykket, så
 * spørgsmålet lander som en tråd i ejerens indbakke. Samme motor som varernes
 * inquiry - vi opfinder ikke en ny beskedkanal.
 */
const askAboutPiecePayloadCreator = (
  { piece, message },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const processAlias = piece?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    return rejectWithValue(
      storableError(new Error('No transaction process attached to collection piece'))
    );
  }
  const [processName] = processAlias.split('/');
  const transitions = getProcess(processName)?.transitions;

  // Den rene inquiry-proces (som samlingsstykker kører på) hedder sin
  // åbnings-transition `inquire-without-payment`; køb/booking/bud-processerne
  // kalder deres `inquire`. Vælg den processen faktisk har, ellers svarer
  // API'et 409 "Invalid transition".
  const inquireTransition = transitions?.INQUIRE_WITHOUT_PAYMENT || transitions?.INQUIRE;
  if (!inquireTransition) {
    return rejectWithValue(
      storableError(new Error('Collection piece process does not support inquiries'))
    );
  }

  return sdk.transactions
    .initiate({
      transition: inquireTransition,
      processAlias,
      params: { listingId: piece.id },
    })
    .then(response => {
      const transactionId = response.data.data.id;
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(setCurrentUserHasOrders());
        return transactionId;
      });
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const askAboutPieceThunk = createAsyncThunk(
  'CollectionPage/askAboutPiece',
  askAboutPiecePayloadCreator
);

export const askAboutPiece = (piece, message) => dispatch =>
  dispatch(askAboutPieceThunk({ piece, message })).unwrap();

// ================ Slice ================ //

const initialState = {
  collectorId: null,
  pieceRefs: [],
  showCollectorError: null,
  queryCollectionError: null,
  askInProgress: false,
  askError: null,
  askSentForPieceId: null,
};

const collectionPageSlice = createSlice({
  name: 'CollectionPage',
  initialState,
  reducers: {
    setInitialState: () => initialState,
    clearAskState: state => {
      state.askError = null;
      state.askSentForPieceId = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(showCollectorThunk.pending, (state, action) => {
        state.showCollectorError = null;
        state.collectorId = action.meta.arg.userId;
      })
      .addCase(showCollectorThunk.rejected, (state, action) => {
        state.showCollectorError = action.payload;
      })
      .addCase(queryCollectionThunk.pending, state => {
        state.queryCollectionError = null;
      })
      .addCase(queryCollectionThunk.fulfilled, (state, action) => {
        state.pieceRefs = action.payload.pieceRefs;
      })
      .addCase(queryCollectionThunk.rejected, (state, action) => {
        state.pieceRefs = [];
        state.queryCollectionError = action.payload;
      })
      .addCase(askAboutPieceThunk.pending, state => {
        state.askInProgress = true;
        state.askError = null;
        state.askSentForPieceId = null;
      })
      .addCase(askAboutPieceThunk.fulfilled, (state, action) => {
        state.askInProgress = false;
        state.askSentForPieceId = action.meta.arg.piece?.id?.uuid || null;
      })
      .addCase(askAboutPieceThunk.rejected, (state, action) => {
        state.askInProgress = false;
        state.askError = action.payload;
      });
  },
});

export const { setInitialState, clearAskState } = collectionPageSlice.actions;
export default collectionPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => dispatch => {
  const userId = new UUID(params.id);
  dispatch(setInitialState());

  return Promise.all([
    dispatch(fetchCurrentUser({ updateHasListings: false, updateNotifications: false })),
    dispatch(showCollectorThunk({ userId })),
    dispatch(queryCollectionThunk({ userId, config })),
  ]);
};
